import React, { useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import API from '../api';
import { toast } from 'react-hot-toast';
import { useTranslation } from '../hooks/useTranslation';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  ChartDataLabels
);

// Initialize socket connection to backend
const SOCKET_URL = process.env.REACT_APP_API_URL?.replace('/api', '');
const socket = io(SOCKET_URL, {
  transports: ['websocket', 'polling'],
  reconnection: true,
  reconnectionDelay: 1000,
  reconnectionAttempts: 5
});

export default function Dashboard() {
  const { getMessage } = useTranslation();
  const [transactions, setTransactions] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    dailyStats: {
      today: {
        vehicles_passed: 0,
        total_revenue: 0,
        transactions_count: 0,
        avg_transaction: 0
      },
      yesterday: {
        vehicles_passed: 0,
        total_revenue: 0,
        transactions_count: 0,
        avg_transaction: 0
      }
    },
    weeklyData: [],
    monthlyData: [],
    deviceTypeStats: [],
    paymentMethodStats: []
  });
  const [selectedPeriod, setSelectedPeriod] = useState('today'); // today, custom
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [wimSummary, setWimSummary] = useState(null);

  // Initialize dates (today)
  useEffect(() => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    setStartDate(todayStr);
    setEndDate(todayStr);
  }, []);

  useEffect(() => {
    socket.on('transaction_update', (data) => {
      setTransactions(prev => [data, ...prev].slice(0, 10)); // Keep only latest 10
      // Also update dashboard data when new transaction comes in
      fetchDashboardData();
    });

    fetchDashboardData();
    fetchWIMSummary();

    return () => socket.disconnect();
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchDashboardData();
    }
  }, [selectedPeriod, startDate, endDate]);

  const fetchDashboardData = async () => {
    if (!startDate || !endDate) return;

    try {
      setLoading(true);
      const response = await API.get(
        `/admin/dashboard-stats?start_date=${startDate}&end_date=${endDate}`
      );
      setDashboardData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
      console.error('Error details:', error.response?.data || error.message);
      setLoading(false);
    }
  };

  const fetchWIMSummary = async () => {
    try {
      const res = await API.get('/wim/summary');
      setWimSummary(res.data);
    } catch (e) {
      // silent
    }
  };

  // Chart configurations
  const revenueChartData = {
    labels: dashboardData.weeklyData?.map(item => item.date) || [],
    datasets: [
      {
        label: getMessage('dashboard.total_revenue'),
        data: dashboardData.weeklyData?.map(item => item.revenue) || [],
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        tension: 0.1,
        fill: true
      },
      {
        label: getMessage('dashboard.vehicle_count'),
        data: dashboardData.weeklyData?.map(item => item.vehicles) || [],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.1,
        yAxisID: 'y1'
      }
    ]
  };

  const revenueChartOptions = {
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: getMessage('dashboard.revenue_lak')
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: getMessage('dashboard.vehicle_count_short')
        },
        grid: {
          drawOnChartArea: false,
        },
      }
    },
    plugins: {
      title: {
        display: true,
        text: getMessage('dashboard.daily_revenue_vehicles')
      }
    }
  };

  const deviceTypeChartData = {
    labels: dashboardData.deviceTypeStats?.map(item =>
      item.device_type === 'mifare' ? getMessage('dashboard.mifare_card') : getMessage('dashboard.obu_sbu')
    ) || [],
    datasets: [
      {
        data: dashboardData.deviceTypeStats?.map(item => item.count) || [],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(147, 51, 234, 0.8)',
          'rgba(34, 197, 94, 0.8)',
          'rgba(251, 191, 36, 0.8)'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  const paymentMethodChartData = {
    labels: dashboardData.paymentMethodStats?.map(item => item.method) || [],
    datasets: [
      {
        label: getMessage('dashboard.usage_count'),
        data: dashboardData.paymentMethodStats?.map(item => item.count) || [],
        backgroundColor: [
          'rgba(34, 197, 94, 0.8)',
          'rgba(59, 130, 246, 0.8)',
          'rgba(251, 191, 36, 0.8)',
          'rgba(239, 68, 68, 0.8)'
        ]
      }
    ]
  };

  const calculatePercentageChange = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  const StatCard = ({ title, value, icon, change, changeType = 'neutral' }) => {
    const changeColor = change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-gray-600';
    const changeIcon = change > 0 ? '📈' : change < 0 ? '📉' : '📊';

    return (
      <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 p-4 sm:p-6 shadow-sm">
        <div className="flex items-start justify-between">
          <p className="text-blue-700 dark:text-blue-300 text-sm sm:text-lg font-semibold">{title}</p>
          <div className="text-2xl sm:text-3xl opacity-20 flex-shrink-0">{icon}</div>
        </div>
        <p className="text-lg sm:text-2xl lg:text-3xl font-bold text-green-800 dark:text-white mt-2">{value}</p>
        {change !== undefined && (
          <p className={`text-xs mt-2 ${changeColor}`}>
            {changeIcon} {change > 0 ? '+' : ''}{change}% {getMessage('dashboard.from_yesterday')}
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 dark:text-white">
            📊 {getMessage('dashboard.title')} - {getMessage('dashboard.system_overview')}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            {getMessage('dashboard.statistics_description')}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 w-full lg:w-auto">
          <select
            value={selectedPeriod}
            onChange={(e) => {
              const period = e.target.value;
              setSelectedPeriod(period);

              if (period === 'today') {
                const today = new Date();
                const todayStr = today.toISOString().split('T')[0];
                setStartDate(todayStr);
                setEndDate(todayStr);
              }
              // For 'custom', keep the current dates and let user pick
            }}
            className="p-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white flex-1 sm:flex-none"
          >
            <option value="today">{getMessage('dashboard.today')}</option>
            <option value="custom">{getMessage('dashboard.custom_range')}</option>
          </select>

          {selectedPeriod === 'custom' && (
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="p-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white flex-1 sm:flex-none"
                max={endDate}
              />
              <span className="flex items-center text-gray-600 dark:text-gray-400">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="p-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white flex-1 sm:flex-none"
                min={startDate}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
          )}

          <button
            onClick={fetchDashboardData}
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-3 sm:px-4 text-sm sm:text-base rounded-lg transition-colors duration-200 flex-shrink-0"
          >
            🔄 {getMessage('dashboard.refresh')}
          </button>
        </div>
      </div>

      {/* Today's Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={getMessage('dashboard.vehicles_today')}
          value={dashboardData.dailyStats?.today?.vehicles_passed?.toLocaleString() || '0'}
          icon="🚗"
          change={calculatePercentageChange(
            dashboardData.dailyStats?.today?.vehicles_passed || 0,
            dashboardData.dailyStats?.yesterday?.vehicles_passed || 0
          )}
        />

        <StatCard
          title={getMessage('dashboard.revenue_today')}
          value={`${(dashboardData.dailyStats?.today?.revenue_today || 0).toLocaleString()} LAK`}
          icon="💰"
          change={calculatePercentageChange(
            dashboardData.dailyStats?.today?.revenue_today || 0,
            dashboardData.dailyStats?.yesterday?.total_revenue || 0
          )}
        />

        <StatCard
          title={getMessage('dashboard.accumulated_amount')}
          value={`${(dashboardData.dailyStats?.today?.total_revenue || 0).toLocaleString()} LAK`}
          icon="📊"
          change={calculatePercentageChange(
            dashboardData.dailyStats?.today?.total_revenue || 0,
            dashboardData.dailyStats?.yesterday?.total_revenue || 0
          )}
        />

        <StatCard
          title={getMessage('dashboard.avg_amount_per_day')}
          value={`${Math.floor(dashboardData.dailyStats?.today?.avg_transaction || 0).toLocaleString()} LAK`}
          icon="💳"
          change={calculatePercentageChange(
            dashboardData.dailyStats?.today?.avg_transaction || 0,
            dashboardData.dailyStats?.yesterday?.avg_transaction || 0
          )}
        />
      </div>

      {/* Vehicle Type Statistics */}
      <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 p-4 mt-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
          🚗 {getMessage('dashboard.vehicle_type_stats') || 'Vehicle Type Statistics'}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-600 dark:text-gray-300">
            <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-600 dark:text-gray-300">
              <tr>
                <th scope="col" className="px-4 py-3">{getMessage('dashboard.vehicle_type') || 'Vehicle Type'}</th>
                <th scope="col" className="px-4 py-3 text-right">{getMessage('dashboard.count') || 'Count'}</th>
                <th scope="col" className="px-4 py-3 text-right">{getMessage('dashboard.amount') || 'Amount (LAK)'}</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData.vehicleTypeStats?.length > 0 ? (
                dashboardData.vehicleTypeStats.map((item, index) => (
                  <tr key={index} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                      {item.vehicle_type || 'Unknown'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {item.count?.toLocaleString() || '0'}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-green-600 dark:text-green-400">
                      {item.amount?.toLocaleString() || '0'} LAK
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                    {getMessage('dashboard.no_data') || 'No data available'}
                  </td>
                </tr>
              )}
              {/* Total Row */}
              {dashboardData.vehicleTypeStats?.length > 0 && (
                <tr className="bg-gray-100 dark:bg-gray-700 font-bold">
                  <td className="px-4 py-3 text-gray-900 dark:text-white">{getMessage('dashboard.total') || 'Total'}</td>
                  <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                    {dashboardData.vehicleTypeStats.reduce((sum, item) => sum + (item.count || 0), 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right text-green-700 dark:text-green-300">
                    {dashboardData.vehicleTypeStats.reduce((sum, item) => sum + (item.amount || 0), 0).toLocaleString()} LAK
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>


      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue and Vehicle Chart */}
        <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 p-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
            📈 {getMessage('dashboard.revenue_and_vehicles')}
          </h3>
          <div style={{ height: '300px' }}>
            <Line data={revenueChartData} options={{ ...revenueChartOptions, maintainAspectRatio: false }} />
          </div>
        </div>

        {/* Device Type Distribution */}
        <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 p-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
            📱 {getMessage('dashboard.device_usage')}
          </h3>
          <div style={{ height: '300px' }}>
            <Doughnut
              data={deviceTypeChartData}
              options={{
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom'
                  },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        const label = context.label || '';
                        const value = context.parsed || 0;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        return `${label}: ${value.toLocaleString()} (${percentage}%)`;
                      }
                    }
                  },
                  datalabels: {
                    display: true,
                    color: '#000',
                    font: {
                      weight: 'bold',
                      size: 14
                    },
                    formatter: (value) => {
                      return value > 0 ? value.toLocaleString() : '';
                    }
                  }
                }
              }}
              plugins={[{
                id: 'centerText',
                beforeDraw: function (chart) {
                  const { ctx, width, height } = chart;
                  const data = chart.data.datasets[0].data;
                  const total = data.reduce((a, b) => a + b, 0);

                  ctx.save();
                  ctx.font = 'bold 24px Arial';
                  ctx.fillStyle = '#374151';
                  ctx.textAlign = 'center';
                  ctx.textBaseline = 'middle';

                  const centerX = width / 2;
                  const centerY = height / 2 - 10;

                  ctx.fillText(total.toLocaleString(), centerX, centerY);

                  ctx.font = '12px Arial';
                  ctx.fillStyle = '#6B7280';
                  ctx.fillText('Total', centerX, centerY + 20);

                  ctx.restore();
                }
              }]}
            />
          </div>
        </div>
      </div>

      {/* Payment Methods Chart */}
      <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 p-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
          💳 {getMessage('dashboard.payment_methods')}
        </h3>
        <div style={{ height: '250px' }}>
          <Bar
            data={paymentMethodChartData}
            options={{
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  display: false
                },
                tooltip: {
                  callbacks: {
                    label: function (context) {
                      const index = context.dataIndex;
                      const stats = dashboardData.paymentMethodStats?.[index];
                      if (stats) {
                        return [
                          `Count: ${stats.count.toLocaleString()}`,
                          `Amount: ${stats.amount.toLocaleString()} LAK`
                        ];
                      }
                      return `Count: ${context.parsed.y.toLocaleString()}`;
                    }
                  }
                },
                datalabels: {
                  display: true,
                  color: '#000',
                  anchor: 'center',
                  align: 'center',
                  font: {
                    weight: 'bold',
                    size: 10
                  },
                  formatter: (value, context) => {
                    const index = context.dataIndex;
                    const stats = dashboardData.paymentMethodStats?.[index];
                    if (stats && value > 0) {
                      return [`${stats.count.toLocaleString()}`, `${stats.amount.toLocaleString()} LAK`];
                    }
                    return value > 0 ? value.toLocaleString() : '';
                  }
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: getMessage('dashboard.usage_count')
                  }
                }
              }
            }}
          />
        </div>
      </div>

      {/* Cash and QR Top-up Statistics */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900 dark:to-blue-900 rounded-lg border-2 border-green-200 dark:border-green-700 p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-white mb-4">
          💵 {getMessage('dashboard.topup_revenue_breakdown')}
        </h2>

        {/* Total Summary */}
        <div className="mt-4 p-3 sm:p-4 bg-gray-100 dark:bg-gray-800 rounded-lg border-t-4 border-green-600 dark:border-green-400">

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <span className="text-base sm:text-lg font-bold text-green-900 dark:text-gray-300">{getMessage('dashboard.total_topup_today')}:</span>
            <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-blue-900 dark:text-white">
              {((dashboardData.dailyStats?.todayCash?.revenue || 0) + (dashboardData.dailyStats?.todayQR?.revenue || 0)).toLocaleString()} LAK
            </span>
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-2 text-xs sm:text-sm gap-1">
            <span className="text-blue-700 dark:text-gray-400">{getMessage('dashboard.cash_topup')}: {((dashboardData.dailyStats?.todayCash?.revenue / ((dashboardData.dailyStats?.todayCash?.revenue || 0) + (dashboardData.dailyStats?.todayQR?.revenue || 1)) * 100) || 0).toFixed(1)}%</span>
            <span className="text-green-700 dark:text-gray-400">QR: {((dashboardData.dailyStats?.todayQR?.revenue / ((dashboardData.dailyStats?.todayCash?.revenue || 0) + (dashboardData.dailyStats?.todayQR?.revenue || 1)) * 100) || 0).toFixed(1)}%</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {/* Cash Top-up Card */}
          <div className="bg-white dark:bg-gray-700 rounded-lg border-2 border-green-500 p-4 sm:p-5 shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base sm:text-lg font-semibold text-green-700 dark:text-green-300">
                💵 {getMessage('dashboard.cash_topup')}
              </h3>
              <div className="text-2xl sm:text-3xl">💵</div>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{getMessage('dashboard.todays_revenue')}</p>
                <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                  {(dashboardData.dailyStats?.todayCash?.revenue || 0).toLocaleString()} LAK
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {dashboardData.dailyStats?.todayCash?.count || 0} {getMessage('dashboard.transactions_count')}
                </p>
              </div>
              <div className="border-t pt-2">
                <p className="text-xs text-gray-600 dark:text-gray-400">{getMessage('dashboard.yesterday')}</p>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {(dashboardData.dailyStats?.yesterdayCash?.revenue || 0).toLocaleString()} LAK
                </p>
                <p className="text-xs text-gray-500">
                  {dashboardData.dailyStats?.yesterdayCash?.count || 0} {getMessage('dashboard.transactions_count')}
                </p>
                <p className="text-xs mt-1 ${
                  calculatePercentageChange(
                    dashboardData.dailyStats?.todayCash?.revenue || 0,
                    dashboardData.dailyStats?.yesterdayCash?.revenue || 0
                  ) > 0 ? 'text-green-600' : 'text-red-600'
                }">
                  {calculatePercentageChange(
                    dashboardData.dailyStats?.todayCash?.revenue || 0,
                    dashboardData.dailyStats?.yesterdayCash?.revenue || 0
                  ) > 0 ? '↑' : '↓'} {Math.abs(calculatePercentageChange(
                    dashboardData.dailyStats?.todayCash?.revenue || 0,
                    dashboardData.dailyStats?.yesterdayCash?.revenue || 0
                  ))}% {getMessage('dashboard.from_yesterday_label')}
                </p>
              </div>
            </div>
          </div>

          {/* QR Top-up Card */}
          <div className="bg-white dark:bg-gray-700 rounded-lg border-2 border-blue-500 p-4 sm:p-5 shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base sm:text-lg font-semibold text-blue-700 dark:text-blue-300">
                📱 {getMessage('dashboard.qr_code_topup')}
              </h3>
              <div className="text-2xl sm:text-3xl">📱</div>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{getMessage('dashboard.todays_revenue')}</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {(dashboardData.dailyStats?.todayQR?.revenue || 0).toLocaleString()} LAK
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {dashboardData.dailyStats?.todayQR?.count || 0} {getMessage('dashboard.transactions_count')}
                </p>
              </div>
              <div className="border-t pt-2">
                <p className="text-xs text-gray-600 dark:text-gray-400">{getMessage('dashboard.yesterday')}</p>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {(dashboardData.dailyStats?.yesterdayQR?.revenue || 0).toLocaleString()} LAK
                </p>
                <p className="text-xs text-gray-500">
                  {dashboardData.dailyStats?.yesterdayQR?.count || 0} {getMessage('dashboard.transactions_count')}
                </p>
                <p className="text-xs mt-1 ${
                  calculatePercentageChange(
                    dashboardData.dailyStats?.todayQR?.revenue || 0,
                    dashboardData.dailyStats?.yesterdayQR?.revenue || 0
                  ) > 0 ? 'text-green-600' : 'text-red-600'
                }">
                  {calculatePercentageChange(
                    dashboardData.dailyStats?.todayQR?.revenue || 0,
                    dashboardData.dailyStats?.yesterdayQR?.revenue || 0
                  ) > 0 ? '↑' : '↓'} {Math.abs(calculatePercentageChange(
                    dashboardData.dailyStats?.todayQR?.revenue || 0,
                    dashboardData.dailyStats?.yesterdayQR?.revenue || 0
                  ))}% {getMessage('dashboard.from_yesterday_label')}
                </p>
              </div>
            </div>
          </div>
        </div>


      </div>


      {/* WIM Summary */}
      {wimSummary && (
        <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 p-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">⚖️ ສະຫຼຸບຄ່າປັບ WIM</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-3 border rounded">
              <div className="text-gray-500 dark:text-gray-400">ຈຳນວນທັງໝົດ</div>
              <div className="text-xl font-bold text-gray-800 dark:text-white">{wimSummary.totals?.total_violations || 0}</div>
            </div>
            <div className="p-3 border rounded">
              <div className="text-gray-500 dark:text-gray-400">ຊຳລະແລ້ວ</div>
              <div className="text-xl font-bold text-green-600">{wimSummary.totals?.paid_count || 0}</div>
            </div>
            <div className="p-3 border rounded">
              <div className="text-gray-500 dark:text-gray-400">ຄ້າງຊຳລະ</div>
              <div className="text-xl font-bold text-red-600">{wimSummary.totals?.unpaid_count || 0}</div>
            </div>
            <div className="p-3 border rounded">
              <div className="text-gray-500 dark:text-gray-400">ຍອດຄ້າງຊຳລະ</div>
              <div className="text-xl font-bold text-red-600">{parseFloat(wimSummary.totals?.unpaid_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} LAK</div>
            </div>
          </div>
        </div>
      )}

      {/* Live Transactions */}
      <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 p-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
          📡 {getMessage('dashboard.live_transactions')}
        </h3>
        {transactions.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-center py-8">
            {getMessage('dashboard.no_transactions')}
          </p>
        ) : (
          <div className="space-y-2">
            {transactions.map((tx, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-600 rounded-lg">
                <div className="flex items-center space-x-3">
                  <span className="text-lg">
                    {tx.method === 'mifare' ? '💳' : tx.method === 'obu' ? '📡' : '💰'}
                  </span>
                  <div>
                    <div className="font-medium text-gray-800 dark:text-white">
                      {tx.method?.toUpperCase() || 'Unknown'} - {tx.status || 'Completed'}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(tx.timestamp).toLocaleString('th-TH')}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-lg text-gray-800 dark:text-white">
                    {tx.amount?.toLocaleString() || '0'} LAK
                  </div>
                  {tx.license_plate && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {tx.license_plate}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
